import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Cookie } from 'lucide-react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    return (
        <motion.div
            className="legal-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>Privacy Policy | Kodebuild</title>
                <meta name="description" content="Kodebuild's Privacy Policy describes how we handle and protect your personal data when you use our website." />
                <link rel="canonical" href="https://kodebuildtech.com/privacy" />
            </Helmet>
            <div className="container">
                <header className="legal-header">
                    <h1>Privacy Policy</h1>
                    <p>Last Updated: March 1, 2026</p>
                </header>

                <div className="legal-content">
                    <section>
                        <div className="legal-icon-title">
                            <Shield className="legal-icon" />
                            <h2>1. Introduction</h2>
                        </div>
                        <p>
                            At Kodebuild, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use,
                            disclose, and safeguard your information when you visit our website. Please read this privacy policy
                            carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
                        </p>
                    </section>

                    <section>
                        <div className="legal-icon-title">
                            <Eye className="legal-icon" />
                            <h2>2. Information We Collect</h2>
                        </div>
                        <p>
                            We may collect information about you in a variety of ways. The information we may collect on the Site includes:
                        </p>
                        <ul>
                            <li><strong>Personal Data:</strong> Personally identifiable information, such as your name, shipping address, email address, and telephone number, that you voluntarily give to us when you contact us.</li>
                            <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access the Site, such as your IP address, your browser type, your operating system, your access times, and the pages you have viewed directly before and after accessing the Site.</li>
                        </ul>
                    </section>

                    <section>
                        <div className="legal-icon-title">
                            <Cookie className="legal-icon" />
                            <h2>3. Cookies and Web Beacons</h2>
                        </div>
                        <p>
                            We may use cookies, web beacons, tracking pixels, and other tracking technologies on the Site to help
                            customize the Site and improve your experience. When you access the Site, your personal information
                            is not collected through the use of tracking technology.
                        </p>
                        <h3>Google AdSense and DoubleClick Cookie</h3>
                        <p>
                            Google, as a third-party vendor, uses cookies to serve ads on our site. Google's use of the DoubleClick
                            cookie enables it and its partners to serve ads to our users based on their visit to our site or
                            other sites on the Internet. You may opt out of the use of the DoubleClick cookie for interest-based
                            advertising by visiting <a href="https://www.google.com/ads/preferences/" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.
                        </p>
                    </section>

                    <section>
                        <div className="legal-icon-title">
                            <Lock className="legal-icon" />
                            <h2>4. Security of Your Information</h2>
                        </div>
                        <p>
                            We use administrative, technical, and physical security measures to help protect your personal information.
                            While we have taken reasonable steps to secure the personal information you provide to us, please be
                            aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data
                            transmission can be guaranteed against any interception or other type of misuse.
                        </p>
                    </section>

                    <section className="legal-booster">
                        <div className="legal-icon-title">
                            <Shield className="legal-icon" />
                            <h2>6. Our Commitment to Innovation</h2>
                        </div>
                        <p>
                            Kodebuild is more than just a software company; we are partners in your digital journey. 
                            Our commitment to privacy is part of our broader mission to build secure, high-performance 
                            systems that drive growth. Whether we are engineering custom web apps or implementing 
                            AI-driven automation, we prioritize your data's integrity and security in every byte.
                        </p>
                    </section>
                </div>
            </div>
        </motion.div>
    );
};

export default PrivacyPolicy;
